/**
 * Feuille de brouillon « fondue dans le décor » (exercice sur la feuille).
 *
 * Plus de popup : les lignes s'écrivent dans la page, juste sous la question,
 * sur le même papier. La palette des bouts vit dans le dock du bas
 * (`SheetPalette`, rendue par le player quand on écrit). Toucher la ligne en
 * cours la fait monter (`onActivate`).
 */

import './scratch-sheet.css';
import { useEffect, useRef } from 'react';
import { Lightbulb } from 'lucide-react';
import type { ScratchSession } from './scratch-session';
import { SheetPaper } from './sheet-paper';
import { useSessionState } from './scratch-sheet';

export interface InlineSheetProps {
  session: ScratchSession;
  /** aperçu tuteur d'une feuille pas encore publiée */
  draft?: boolean;
  /** la palette est dans le dock (on écrit) */
  writing: boolean;
  /** toucher la ligne en cours / « Écrire » : la palette monte */
  onActivate: () => void;
}

export function InlineSheet({ session, draft, writing, onActivate }: InlineSheetProps) {
  const state = useSessionState(session);
  const guard = useRef(false);
  const rootRef = useRef<HTMLElement>(null);

  // la palette monte : la ligne en cours remonte juste au-dessus du dock
  // (deux images : le dock a d'abord publié sa nouvelle hauteur `--dock-h`)
  useEffect(() => {
    if (!writing) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        rootRef.current
          ?.querySelector('.sc-cur')
          ?.scrollIntoView?.({ block: 'end', behavior: 'smooth' });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [writing]);

  // toucher ailleurs (page OU dock) termine l'insertion / sort de la fraction ;
  // le geste touché s'exécute ensuite normalement (comme dans le popup)
  useEffect(() => {
    if (!writing) return;
    const onDown = () => {
      guard.current = false;
    };
    const onClick = (e: MouseEvent) => {
      if (guard.current) return;
      const s = session.getSnapshot();
      if (s.insertAt === null && !s.cursor) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-sc-keep="all"], [data-k]')) return;
      if (s.cursor && t?.closest?.('[data-sc-keep="frac"]')) return;
      session.finishEditing();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [writing, session]);

  return (
    <section
      ref={rootRef}
      // la feuille a sa propre gouttière (marge rouge) : elle se cale sur celle
      // de la page (colonne à 52 px) au lieu de s'y ajouter
      className="sc-root sc-inline -ml-[52px]"
      aria-label="Mon brouillon"
      data-testid="inline-sheet"
    >
      {/* pas de titre ni de mode d'emploi : la feuille se comprend d'elle-même
          (prise en main au 1er exercice) ; restent les HORA et le coup de pouce */}
      {(draft || !session.free) && (
        <div className="flex items-center justify-end gap-2 pl-[54px] pr-1 pt-1">
          {draft && (
            <span className="font-display mr-auto rounded-full border border-dashed border-[var(--sc-line-2)] px-2 py-0.5 text-[11px] font-semibold text-[var(--sc-muted)]">
              Aperçu · non publiée
            </span>
          )}
          {!session.free && (
            <>
              <span
                className="sc-hora whitespace-nowrap"
                aria-label={`${state.hora} HORA gagnés sur cette feuille`}
                data-testid="scratch-hora"
              >
                +{state.hora} HORA
              </span>
              <button
                type="button"
                className="sc-icon-btn"
                data-hint=""
                aria-label="Coup de pouce"
                disabled={state.locked}
                onClick={() => {
                  onActivate();
                  session.hint();
                }}
              >
                <Lightbulb className="h-5 w-5" aria-hidden />
              </button>
            </>
          )}
        </div>
      )}
      <SheetPaper state={state} session={session} guard={guard} inline onActivate={onActivate} />
    </section>
  );
}
